import { SectionHeader, SectionTitle, Body } from "@sprout/typography";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";
import Divider from "@sprout/divider.tsx";
import useTranslation from "@hooks/use_translation.ts";
import ApplicationFormHeader from "@components/job_post/application_form_header.tsx";
import { ConfirmationRecommendations } from "@components/job_post/confirmation_recommendations.tsx";
import { router } from "@inertiajs/react";
import type { JobPost, SearchSpecificFields } from "@/types/jben/job_post.ts";

interface Props {
  jobPost: JobPost & SearchSpecificFields;
  jobRecommendationsEnabled?: boolean;
  onViewJobs: () => void;
}

export const ApplicationSuccess = ({
  jobPost,
  jobRecommendationsEnabled = false,
  onViewJobs,
}: Props) => {
  const { t } = useTranslation("jobSearch");

  if (jobRecommendationsEnabled) {
    return (
      <div className="flex flex-col items-start gap-4">
        <div className="pr-10">
          <SectionTitle large>
            {t("applicationSuccess.headerForRecommendations", {
              title: jobPost.title,
              company: jobPost.company_name,
            })}
          </SectionTitle>
        </div>
        <Body>{t("applicationSuccess.bodyForRecommendations")}</Body>
        <ConfirmationRecommendations appliedJobPostId={jobPost.id} />
        <div className="-mx-8 w-[calc(100%+4rem)]">
          <Divider />
        </div>
        <div className="flex flex-row justify-end gap-3 w-full">
          <SecondaryButton onClick={() => router.visit("/applications")}>
            {t("applicationSuccess.viewApplication")}
          </SecondaryButton>
          <PrimaryButton onClick={onViewJobs}>
            {t("applicationSuccess.exploreAllJobs")}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  return (
    <>
      <ApplicationFormHeader
        title={jobPost.title}
        companyName={jobPost.company_name}
        logoUrl={jobPost.logo_url}
        publicUrl={jobPost.public_url}
        firstPublished={jobPost.first_published}
        locations={jobPost.locations}
        workType={jobPost.work_type}
        payRange={jobPost.pay_range_string ?? undefined}
      />
      <div className="flex flex-col items-start gap-4 mt-6">
        <SectionHeader as="h2">{t("applicationSuccess.header")}</SectionHeader>
        <Body>{t("applicationSuccess.body")}</Body>
        <SecondaryButton
          onClick={() =>
            router.visit(`/jobs/search?query=${encodeURIComponent(jobPost.company_name)}`)
          }
        >
          {t("applicationSuccess.viewMoreJobs", { company: jobPost.company_name })}
        </SecondaryButton>
      </div>
    </>
  );
};
